<!--
  ChatPanel — LLM-driven parametric modeling assistant.
  Wired to fal.run/openrouter/router with default openai/gpt-5.5.
  Conversation kept client-side; replayed as plain text per turn.

  Image-mode flow: when the user attaches a reference photo, the message
  routes through runVisionPipeline() which (1) calls gpt-image-2/edit to
  synthesize a 4-view technical sheet, then (2) calls gpt-5.5 vision with
  both images + node registry, returning the parametric graph actions.
-->
<script lang="ts">
  import { onMount } from 'svelte';
  import Icon from './lib/Icon.svelte';
  import { callFal, testConnection, parseModelOutput } from './llm/falProvider';
  import { dispatchTool, SYSTEM_PROMPT, makePlaceholderResolver, buildGraphContext, evaluateParamGraph } from './llm/graphTools';
  import { runVisionPipeline, type PipelineStage } from './llm/imagePipeline';
  import { chatStore, setHistory, appendMessage, clearChatHistory } from './llm/chatStore.svelte';
  import { app } from './store.svelte';

  let { onClose }: { onClose?: () => void } = $props();

  let apiKey = $state(localStorage.getItem('clayforge.fal.apiKey') ?? '');
  let model = $state(localStorage.getItem('clayforge.fal.model') ?? 'openai/gpt-5.5');
  let showSettings = $state(!apiKey);

  /** Curated OpenRouter slugs — verified working via fal.run/openrouter/router.
   *  Full catalog: 200+ models at https://openrouter.ai/models */
  const MODEL_PRESETS = [
    'openai/gpt-5.5',
    'openai/gpt-5',
    'openai/gpt-5-chat',
    'openai/gpt-5-mini',
    'openai/gpt-4.1',
    'openai/gpt-4o',
    'openai/o3',
    'anthropic/claude-opus-4.6',
    'anthropic/claude-sonnet-4.6',
    'anthropic/claude-sonnet-4.5',
    'anthropic/claude-3.7-sonnet',
    'google/gemini-2.5-pro',
    'google/gemini-2.5-flash',
    'meta-llama/llama-4-maverick',
    'meta-llama/llama-4-scout',
    'deepseek/deepseek-r1',
    'x-ai/grok-4',
    'qwen/qwen-2.5-72b-instruct',
    'mistralai/mistral-large'
  ];

  let testResult = $state<string | null>(null);
  let testError = $state('');
  let testing = $state(false);

  // Shared state — survives mode switches and panel toggles. Read directly
  // from chatStore so we re-render when other instances mutate it.
  let history = $derived(chatStore.history);
  let inputText = $state('');
  let busy = $derived(chatStore.busy);
  let lastError = $state(chatStore.lastError);
  let scrollEl: HTMLDivElement | undefined = $state();

  // ===== Image attachment + pipeline state =====
  let pendingImage = $state<File | null>(null);
  let pendingImagePreview = $state<string | null>(null);
  let fileInput: HTMLInputElement | undefined = $state();
  let dragHover = $state(false);
  let pipelineStage = $derived(chatStore.pipelineStage);

  function saveSettings() {
    localStorage.setItem('clayforge.fal.apiKey', apiKey);
    localStorage.setItem('clayforge.fal.model', model);
    showSettings = false;
  }

  async function runTest() {
    if (!apiKey) { testError = 'API anahtarı boş'; return; }
    testing = true;
    testResult = null;
    testError = '';
    try {
      const out = await testConnection(apiKey, model);
      testResult = out;
    } catch (err) {
      testError = (err as Error).message;
    } finally {
      testing = false;
    }
  }

  /** Pack the visible history + new user message into a single prompt.
   *  Trim aggressively to limit token spend:
   *  - Only last MAX_TURNS turns
   *  - Strip data URIs (replace with "[görsel]")
   *  - Truncate each message body to 600 chars (assistant <actions> can be long)
   *  - Snapshot is already compact (~80 bytes per node) */
  const MAX_TURNS = 6;
  const MAX_BODY_CHARS = 600;
  function buildPrompt(userMsg: string): string {
    const ctxBlock = buildGraphContext();
    const recent = history.slice(-MAX_TURNS);
    const turns = recent.map((m) => {
      let body = m.content;
      if (body.length > MAX_BODY_CHARS) body = body.slice(0, MAX_BODY_CHARS) + '…';
      const tag = m.role === 'user' ? 'KULLANICI' : 'ASİSTAN';
      return `[${tag}] ${body}`;
    }).join('\n\n');
    const ctx = recent.length ? `${ctxBlock}\n\nÖnceki konuşma:\n${turns}\n\n` : `${ctxBlock}\n\n`;
    return `${ctx}[KULLANICI] ${userMsg}`;
  }

  /** Rough cost estimator for the user-visible warning. ~4 chars/token,
   *  pricing assumes gpt-5.5 levels (~$2.50/1M input, $10/1M output).
   *  Not exact — surfaces the order of magnitude so the user can decide
   *  whether to send. */
  function estimateRequestCostUsd(promptText: string, sysText: string, maxOut: number): number {
    const inTokens = Math.ceil((promptText.length + sysText.length) / 4);
    const outTokens = maxOut;
    return (inTokens / 1_000_000) * 2.5 + (outTokens / 1_000_000) * 10;
  }

  // ===== Composer auto-grow + drag-resize =====
  /** Hard ceiling per request — caps the model's output. 4096 is enough
   *  for a complex graph generation. Without this a runaway model can
   *  output 30k+ tokens unchecked. */
  const MAX_TOKENS = 4096;
  /** Soft ceiling per single request (USD). Triggers a confirm dialog. */
  const COST_WARN_USD = 0.25;
  /** User-set manual height (px). Persisted across sessions. When null, the
   *  textarea auto-grows with content up to AUTO_MAX_PX. */
  let manualHeight = $state<number | null>(
    (() => { const v = localStorage.getItem('clayforge.chat.composerH'); return v ? parseInt(v, 10) : null; })()
  );
  let textareaEl: HTMLTextAreaElement | undefined = $state();
  const AUTO_MIN_PX = 36;
  const AUTO_MAX_PX = 320;

  /** Auto-resize on every input — scrollHeight reflects natural content
   *  height; we clamp into [min, max]. Skipped while a manual height is set. */
  function autoGrow() {
    if (!textareaEl || manualHeight !== null) return;
    textareaEl.style.height = 'auto';
    const next = Math.min(AUTO_MAX_PX, Math.max(AUTO_MIN_PX, textareaEl.scrollHeight));
    textareaEl.style.height = next + 'px';
  }

  /** Apply the manual height when set, or kick auto-grow when cleared. */
  $effect(() => {
    if (!textareaEl) return;
    if (manualHeight !== null) {
      textareaEl.style.height = manualHeight + 'px';
    } else {
      autoGrow();
    }
  });
  // Re-grow when text changes
  $effect(() => { void inputText; if (manualHeight === null) autoGrow(); });

  // Drag-to-resize handle on the top edge of the composer.
  let resizing = $state(false);
  let resizeStart = { y: 0, h: 0 };
  function startResize(e: PointerEvent) {
    if (!textareaEl) return;
    e.preventDefault();
    resizing = true;
    resizeStart = { y: e.clientY, h: textareaEl.offsetHeight };
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
  }
  function onResize(e: PointerEvent) {
    if (!resizing || !textareaEl) return;
    // Dragging up should grow the box, so subtract.
    const next = Math.min(480, Math.max(36, resizeStart.h - (e.clientY - resizeStart.y)));
    manualHeight = next;
    textareaEl.style.height = next + 'px';
  }
  function endResize(e: PointerEvent) {
    if (!resizing) return;
    resizing = false;
    (e.currentTarget as HTMLElement).releasePointerCapture(e.pointerId);
    if (manualHeight !== null) localStorage.setItem('clayforge.chat.composerH', String(manualHeight));
  }
  /** Double-click the handle → release to auto-grow mode. */
  function clearManualHeight() {
    manualHeight = null;
    localStorage.removeItem('clayforge.chat.composerH');
    autoGrow();
  }

  /** Live estimate that updates as the user types — shown as a small badge
   *  in the composer so they can see what a send will cost. */
  let liveEstUsd = $derived.by(() => {
    if (!apiKey) return 0;
    const t = inputText.trim();
    if (!t && !pendingImage) return 0;
    const fp = pendingImage ? t : buildPrompt(t);
    const base = estimateRequestCostUsd(fp, SYSTEM_PROMPT, MAX_TOKENS);
    return pendingImage ? base + 0.10 : base;
  });

  /** No-op now — earlier we auto-jumped to param mode so the user could see
   *  the graph being drawn, but the per-mode overlay already shows live mesh
   *  updates everywhere, so forcing a mode switch is intrusive. Kept as a
   *  named hook in case we want to bring it back behind a setting. */
  function ensureParamMode() { /* intentionally empty */ }

  async function executeActions(actions: { tool: string; args: Record<string, unknown> }[]): Promise<string[]> {
    const resolver = makePlaceholderResolver();
    let addCounter = 0;
    const summaries: string[] = [];
    for (const a of actions) {
      const args: Record<string, unknown> = { ...a.args };
      for (const k of ['nodeId', 'fromNode', 'toNode']) {
        if (typeof args[k] === 'string') args[k] = resolver.resolve(args[k] as string);
      }
      const res = await dispatchTool(a.tool, args);
      if (a.tool === 'add_node') {
        addCounter += 1;
        const placeholder = `n${addCounter}`;
        if (res.nodeId) resolver.remember(placeholder, res.nodeId);
      }
      summaries.push(`${res.ok ? '✓' : '✗'} ${a.tool}: ${res.message}`);
    }
    
    // Evaluate the graph to ensure the mesh is built even if ParamEditor is not mounted
    if (actions.length > 0) {
      evaluateParamGraph();
    }
    
    return summaries;
  }

  async function send() {
    const text = inputText.trim();
    if ((!text && !pendingImage) || busy) return;
    if (!apiKey) { showSettings = true; lastError = 'API anahtarı gerekli'; return; }

    const file = pendingImage;
    const previewUri = pendingImagePreview;

    // Pre-flight cost guard. Image-mode is much pricier (gpt-image-2 alone
    // is ~$0.04-0.08 per call, plus the vision LLM round-trip). We always
    // confirm those, plus any text request that would burn over $0.25.
    const fullPrompt = file ? text : buildPrompt(text);
    const estUsd = file
      ? 0.10 + estimateRequestCostUsd(fullPrompt, SYSTEM_PROMPT, MAX_TOKENS)
      : estimateRequestCostUsd(fullPrompt, SYSTEM_PROMPT, MAX_TOKENS);
    if (file) {
      if (!confirm(`Görsel pipeline ~$${estUsd.toFixed(3)} tüketir (gpt-image-2 + vision LLM). Devam?`)) return;
    } else if (estUsd > COST_WARN_USD) {
      if (!confirm(`Bu istek ~$${estUsd.toFixed(3)} tüketebilir (büyük graph snapshot + uzun yanıt sınırı). Devam?`)) return;
    }

    inputText = '';
    pendingImage = null;
    pendingImagePreview = null;
    lastError = '';
    chatStore.lastError = '';
    chatStore.pipelineStage = null;

    appendMessage({
      role: 'user',
      content: text || (file ? '(görsel referans)' : ''),
      imageDataUri: previewUri ?? undefined
    });
    chatStore.busy = true;
    ensureParamMode();

    try {
      if (file) {
        // Image-mode pipeline: 2 stages.
        const ctxText = chatStore.history.length > 1
          ? chatStore.history.slice(0, -1).map((m) =>
              m.role === 'user' ? `[KULLANICI] ${m.content}` : `[ASİSTAN] ${m.content}`
            ).join('\n\n')
          : '';
        const graphCtx = buildGraphContext();
        const result = await runVisionPipeline({
          apiKey,
          model,
          userPrompt: text || 'Bu objeyi parametrik node graph olarak yeniden üret.',
          imageFile: file,
          conversationContext: `${graphCtx}\n\n${ctxText}`.trim(),
          onStage: (s) => { chatStore.pipelineStage = s; }
        });
        const summaries = await executeActions(result.actions);
        appendMessage({
          role: 'assistant',
          content: result.reply || (summaries.length > 0 ? '✓ Model üretildi' : '(boş yanıt)'),
          actions: summaries.length > 0 ? summaries : undefined,
          sheetDataUri: result.sheetDataUri
        });
      } else {
        // Text-only path.
        const raw = await callFal({
          apiKey, model,
          systemPrompt: SYSTEM_PROMPT,
          prompt: buildPrompt(text),
          maxTokens: MAX_TOKENS
        });
        const { actions, reply } = parseModelOutput(raw.output);
        const summaries = await executeActions(actions);
        appendMessage({
          role: 'assistant',
          content: reply || (summaries.length > 0 ? '✓ Tamam' : raw.output),
          actions: summaries.length > 0 ? summaries : undefined
        });
      }
    } catch (err) {
      lastError = (err as Error).message;
      chatStore.lastError = lastError;
      appendMessage({ role: 'assistant', content: `❌ ${lastError}` });
    } finally {
      chatStore.busy = false;
      chatStore.pipelineStage = null;
      requestAnimationFrame(() => {
        if (scrollEl) scrollEl.scrollTop = scrollEl.scrollHeight;
      });
    }
  }

  function clearHistory() {
    if (!confirm('Sohbet geçmişini temizle?')) return;
    clearChatHistory();
  }

  function onKey(e: KeyboardEvent) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  }

  // ===== Image attachment helpers =====
  async function attachFile(file: File) {
    if (!file.type.startsWith('image/')) {
      lastError = 'Sadece görsel dosya kabul edilir (PNG/JPG/WebP).';
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      lastError = 'Görsel 10 MB\'dan büyük olamaz.';
      return;
    }
    pendingImage = file;
    pendingImagePreview = await new Promise<string>((resolve, reject) => {
      const r = new FileReader();
      r.onerror = () => reject(new Error('Resim okunamadı'));
      r.onload = () => resolve(r.result as string);
      r.readAsDataURL(file);
    });
    lastError = '';
  }

  function clearPending() {
    pendingImage = null;
    pendingImagePreview = null;
  }

  function onFileChange(e: Event) {
    const f = (e.target as HTMLInputElement).files?.[0];
    if (f) attachFile(f);
    if (fileInput) fileInput.value = '';
  }

  function onDragEnter(e: DragEvent) {
    if (!e.dataTransfer?.types?.includes('Files')) return;
    e.preventDefault();
    dragHover = true;
  }
  function onDragOver(e: DragEvent) {
    if (!e.dataTransfer?.types?.includes('Files')) return;
    e.preventDefault();
  }
  function onDragLeave(e: DragEvent) {
    if ((e.relatedTarget as Node | null) && (e.currentTarget as Node).contains(e.relatedTarget as Node)) return;
    dragHover = false;
  }
  function onDrop(e: DragEvent) {
    e.preventDefault();
    dragHover = false;
    const f = e.dataTransfer?.files?.[0];
    if (f) attachFile(f);
  }
  function onPaste(e: ClipboardEvent) {
    const item = Array.from(e.clipboardData?.items ?? []).find((it) => it.type.startsWith('image/'));
    if (!item) return;
    const f = item.getAsFile();
    if (f) { attachFile(f); e.preventDefault(); }
  }

  function stageLabel(s: PipelineStage): string {
    switch (s.kind) {
      case 'start': return 'Başlatılıyor…';
      case 'multiview-pending': return '🎨 Teknik görünümler üretiliyor (Stage 1/2)…';
      case 'multiview-done':    return '✓ Teknik sayfa hazır → vision analizine geçiliyor';
      case 'synthesis-pending': return '🧠 Geometri analiz ediliyor + node graph kuruluyor (Stage 2/2)…';
      case 'synthesis-done':    return '✓ Tamamlandı';
      case 'error':             return `❌ ${s.stage} aşaması hatası: ${s.message}`;
    }
  }

  const examples = [
    '15 cm yüksekliğinde, 6 dilim simetrik kulplu bir vazo',
    'Bir küre + 4 koni dizisi ile masa lambası',
    'Vazonun yüksekliğini 200\'e çıkar',
    'Bu fotoğraftaki vazoyu yeniden üret (görsel ekle ↓)'
  ];

  onMount(() => {
    if (scrollEl) scrollEl.scrollTop = scrollEl.scrollHeight;
    window.addEventListener('paste', onPaste);
    return () => window.removeEventListener('paste', onPaste);
  });
</script>

<div class="chat-panel" role="dialog" aria-label="LLM Asistan"
  ondragenter={onDragEnter} ondragover={onDragOver} ondragleave={onDragLeave} ondrop={onDrop}
  class:drag-hover={dragHover}
>
  <div class="head">
    <div class="title">
      <span class="dot"></span>
      <Icon name="sparkles" size={14} />
      <strong>AI Asistan</strong>
      <span class="model-tag" title="Aktif model">{model}</span>
    </div>
    <div class="head-actions">
      <button class="ico-btn" onclick={() => (showSettings = !showSettings)} title="Ayarlar">
        <Icon name="settings" size={14} />
      </button>
      <button class="ico-btn" onclick={clearHistory} title="Geçmişi temizle"
        disabled={history.length === 0}>
        <Icon name="trash" size={14} />
      </button>
      {#if onClose}
        <button class="ico-btn" onclick={onClose} title="Kapat">
          <Icon name="x" size={14} />
        </button>
      {/if}
    </div>
  </div>

  {#if showSettings}
    <div class="settings">
      <label class="set-row">
        <span>FAL.AI API Key</span>
        <input type="password" bind:value={apiKey}
          placeholder="fal-xxx... (https://fal.ai/dashboard/keys)" />
      </label>
      <label class="set-row">
        <span>Model</span>
        <div class="model-input">
          <input type="text" bind:value={model} placeholder="örn. openai/gpt-5-chat" />
          <select onchange={(e) => { model = (e.currentTarget as HTMLSelectElement).value; }}
            value={model} title="Hızlı seçim">
            {#each MODEL_PRESETS as p}
              <option value={p}>{p}</option>
            {/each}
          </select>
        </div>
      </label>
      <div class="set-actions">
        <button class="ghost" onclick={runTest} disabled={!apiKey || testing}>
          {#if testing}
            <Icon name="loader" size={12} class="spin-icon" /> Test ediyor...
          {:else}
            <Icon name="zap" size={12} /> Test Bağlantı
          {/if}
        </button>
        <button class="primary" onclick={saveSettings} disabled={!apiKey}>
          <Icon name="check" size={12} /> Kaydet
        </button>
      </div>
      {#if testResult !== null}
        <div class="test-ok">
          <Icon name="check" size={11} />
          <strong>Çalışıyor</strong> — model yanıtı: <code>{testResult.slice(0, 100)}</code>
        </div>
      {/if}
      {#if testError}
        <div class="test-err">
          <Icon name="alert-circle" size={11} />
          <span>{testError}</span>
        </div>
      {/if}
      <p class="note">
        API anahtarın sadece tarayıcında saklanır (localStorage). İstekler doğrudan
        fal.run'a gider. Görsel modu: gpt-image-2/edit (Stage 1) + seçtiğin model (Stage 2).
        Maliyet ~$0.04-0.08/görsel.
      </p>
    </div>
  {/if}

  <div class="messages" bind:this={scrollEl}>
    {#if history.length === 0 && !showSettings}
      <div class="empty">
        <p class="hint">
          <Icon name="sparkles" size={13} />
          Türkçe ya da İngilizce, modelini tarif et — graph kendiliğinden çizilir.
        </p>
        <p class="hint" style="margin-top: 8px;">
          <Icon name="image" size={13} />
          <strong>Yeni:</strong> görsel ekle → 4-görünümlü teknik sayfa üretilir → AI analiz edip parametrik model kurar.
        </p>
        <div class="examples">
          {#each examples as ex}
            <button class="ex-chip" onclick={() => { inputText = ex; }}>{ex}</button>
          {/each}
        </div>
      </div>
    {/if}
    {#each history as m, i (i)}
      <div class="msg" class:user={m.role === 'user'} class:assistant={m.role === 'assistant'}>
        <div class="bubble">
          {#if m.imageDataUri}
            <img class="msg-img" src={m.imageDataUri} alt="referans" />
          {/if}
          <div class="content">{m.content}</div>
          {#if m.sheetDataUri}
            <details class="sheet-block" open>
              <summary><Icon name="layout-grid" size={11} /> Üretilen teknik sayfa</summary>
              <a href={m.sheetDataUri} target="_blank" rel="noopener">
                <img class="sheet-img" src={m.sheetDataUri} alt="multi-view" />
              </a>
            </details>
          {/if}
          {#if m.actions && m.actions.length > 0}
            <details class="tool-log">
              <summary><Icon name="workflow" size={10} /> {m.actions.length} işlem</summary>
              <ul>
                {#each m.actions as t}
                  <li><code>{t}</code></li>
                {/each}
              </ul>
            </details>
          {/if}
        </div>
      </div>
    {/each}
    {#if busy}
      <div class="msg assistant">
        <div class="bubble loading">
          <Icon name="loader" size={12} class="spin-icon" />
          {pipelineStage ? stageLabel(pipelineStage) : 'düşünüyor...'}
          {#if pipelineStage?.kind === 'multiview-done'}
            <a href={pipelineStage.sheetDataUri} target="_blank" rel="noopener" class="stage-thumb-link">
              <img src={pipelineStage.sheetDataUri} alt="" class="stage-thumb" />
            </a>
          {/if}
        </div>
      </div>
    {/if}
  </div>

  {#if pendingImagePreview}
    <div class="pending-image">
      <img src={pendingImagePreview} alt="pending" />
      <div class="pending-info">
        <div class="pending-name">{pendingImage?.name ?? 'görsel'}</div>
        <div class="pending-size">{((pendingImage?.size ?? 0) / 1024).toFixed(0)} KB · gönderince 2-stage pipeline çalışır</div>
      </div>
      <button class="ico-btn small" onclick={clearPending} title="Görseli kaldır">
        <Icon name="x" size={11} />
      </button>
    </div>
  {/if}

  <div class="composer">
    <input type="file" accept="image/*" bind:this={fileInput} onchange={onFileChange} style="display:none" />
    <div class="resize-handle" onpointerdown={startResize} onpointermove={onResize} onpointerup={endResize} onpointercancel={endResize}
      ondblclick={clearManualHeight} title="Sürükle: yeniden boyutlandır · Çift tık: otomatik büyüme"></div>
    <div class="composer-row">
    <button class="attach" onclick={() => fileInput?.click()} disabled={!apiKey || busy}
      title="Görsel ekle (paste / drop da çalışır)">
      <Icon name="image" size={14} />
    </button>
    <textarea
      bind:this={textareaEl}
      bind:value={inputText}
      oninput={autoGrow}
      onkeydown={onKey}
      placeholder={apiKey ? (pendingImage ? 'Bu görsel hakkında not ekle (opsiyonel) — Enter gönder' : 'Modeli tarif et — Enter gönder, Shift+Enter yeni satır') : 'Önce ayarlardan API anahtarını gir'}
      disabled={!apiKey || busy}
    ></textarea>
    <button class="send" onclick={send} disabled={!apiKey || busy || (!inputText.trim() && !pendingImage)}
      title={liveEstUsd > 0 ? `~$${liveEstUsd.toFixed(3)} tahmini maliyet` : 'Gönder'}>
      {#if liveEstUsd > 0}
        <span class="cost-badge" class:warn={liveEstUsd > COST_WARN_USD}>${liveEstUsd.toFixed(3)}</span>
      {:else}
        <Icon name="zap" size={14} />
      {/if}
    </button>
    </div>
  </div>
  {#if lastError && !showSettings}
    <div class="err-bar"><Icon name="alert-circle" size={12} /> {lastError}</div>
  {/if}
  {#if dragHover}
    <div class="drop-overlay">
      <Icon name="image" size={28} />
      <span>Görseli buraya bırak</span>
    </div>
  {/if}
</div>

<style>
  .chat-panel {
    width: 340px;
    height: 100%;
    display: flex; flex-direction: column;
    background: linear-gradient(180deg, #0d1828, #0a1322);
    border: 1px solid var(--border-soft);
    border-radius: 12px;
    overflow: hidden;
    box-shadow: -16px 0 32px -16px rgba(0,0,0,0.5);
    position: relative;
  }
  .chat-panel.drag-hover { outline: 2px dashed var(--accent); outline-offset: -8px; }
  .head {
    display: flex; align-items: center; justify-content: space-between;
    padding: 10px 12px;
    border-bottom: 1px solid var(--border-soft);
    background: rgba(255,255,255,0.02);
  }
  .title { display: inline-flex; align-items: center; gap: 7px; color: var(--text-bright); font-size: 12px; }
  .title .dot {
    width: 7px; height: 7px; border-radius: 50%;
    background: var(--ok);
    box-shadow: 0 0 0 2px rgba(52,211,153,0.18), 0 0 6px rgba(52,211,153,0.6);
  }
  .model-tag {
    margin-left: 4px; font-size: 10px; font-weight: 500;
    color: var(--text-3); padding: 2px 6px;
    background: var(--chip-bg); border-radius: 4px;
  }
  .head-actions { display: flex; gap: 2px; }
  .ico-btn {
    width: 26px; height: 26px;
    display: inline-flex; align-items: center; justify-content: center;
    background: transparent; border: 1px solid transparent; border-radius: 6px;
    color: var(--text-3); cursor: pointer;
    transition: background 0.12s, color 0.12s;
  }
  .ico-btn:hover { background: rgba(255,255,255,0.06); color: var(--text-bright); }
  .ico-btn:disabled { opacity: 0.35; cursor: not-allowed; }
  .ico-btn.small { width: 22px; height: 22px; }

  .settings {
    padding: 12px;
    border-bottom: 1px solid var(--border-soft);
    background: rgba(245,158,75,0.04);
    display: flex; flex-direction: column; gap: 10px;
  }
  .set-row { display: flex; flex-direction: column; gap: 4px; font-size: 11px; color: var(--text-2); }
  .set-row input {
    padding: 6px 9px; font-size: 11px;
    background: var(--bg-3); color: var(--text-bright);
    border: 1px solid var(--border-soft); border-radius: 6px;
    font-family: inherit;
  }
  .set-row input:focus { outline: none; border-color: var(--accent); }
  .model-input { display: flex; gap: 4px; align-items: stretch; }
  .model-input input { flex: 1; }
  .model-input select {
    width: 32px; padding: 0; font-size: 0;
    background: var(--bg-3); color: var(--text-bright);
    border: 1px solid var(--border-soft); border-radius: 6px;
    cursor: pointer;
    background-image: url("data:image/svg+xml;utf8,%3Csvg xmlns='http://www.w3.org/2000/svg' width='14' height='14' viewBox='0 0 24 24' fill='none' stroke='%238aa6c2' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='m6 9 6 6 6-6'/%3E%3C/svg%3E");
    background-repeat: no-repeat; background-position: center;
    appearance: none;
  }
  .model-input select option { font-size: 11px; }
  .set-actions { display: flex; justify-content: flex-end; gap: 6px; }
  .set-actions .primary {
    display: inline-flex; align-items: center; gap: 5px;
    padding: 6px 12px; font-size: 11px; font-weight: 600;
    background: linear-gradient(180deg, #1f9d6f, #168558);
    color: white; border: 1px solid rgba(255,255,255,0.1); border-radius: 6px;
    cursor: pointer; font-family: inherit;
  }
  .set-actions .primary:disabled { opacity: 0.4; cursor: not-allowed; }
  .set-actions .ghost {
    display: inline-flex; align-items: center; gap: 5px;
    padding: 6px 12px; font-size: 11px; font-weight: 600;
    background: var(--chip-bg); color: var(--text-2);
    border: 1px solid var(--border-soft); border-radius: 6px;
    cursor: pointer; font-family: inherit;
  }
  .set-actions .ghost:hover:not(:disabled) { background: var(--chip-bg-hover); color: var(--text-bright); }
  .set-actions .ghost:disabled { opacity: 0.4; cursor: not-allowed; }
  .test-ok {
    display: flex; align-items: center; gap: 6px;
    padding: 7px 9px; font-size: 11px; line-height: 1.4;
    background: rgba(52,211,153,0.12); color: #6ee7b7;
    border: 1px solid rgba(52,211,153,0.32); border-radius: 6px;
  }
  .test-ok code {
    font-family: ui-monospace, monospace; font-size: 10.5px;
    background: rgba(0,0,0,0.25); padding: 1px 5px; border-radius: 3px;
  }
  .test-err {
    display: flex; align-items: flex-start; gap: 6px;
    padding: 7px 9px; font-size: 11px; line-height: 1.4;
    background: rgba(208,74,58,0.12); color: #ffaaa0;
    border: 1px solid rgba(208,74,58,0.32); border-radius: 6px;
    word-break: break-word; white-space: pre-wrap;
  }
  .note { margin: 0; font-size: 10px; color: var(--text-muted); line-height: 1.5; }

  .messages {
    flex: 1; overflow-y: auto;
    padding: 12px; display: flex; flex-direction: column; gap: 10px;
    scrollbar-width: thin;
  }
  .empty { padding: 12px 0; }
  .empty .hint {
    display: inline-flex; align-items: center; gap: 6px;
    color: var(--text-3); font-size: 11.5px; margin: 0;
  }
  .empty .hint strong { color: var(--accent); }
  .examples { display: flex; flex-direction: column; gap: 6px; margin-top: 12px; }
  .ex-chip {
    text-align: left; font-size: 11px; line-height: 1.4;
    padding: 8px 10px;
    background: rgba(255,255,255,0.03);
    border: 1px solid var(--border-soft); border-radius: 8px;
    color: var(--text-2); cursor: pointer; font-family: inherit;
    transition: background 0.12s, border-color 0.12s;
  }
  .ex-chip:hover { background: rgba(74,184,255,0.08); border-color: rgba(74,184,255,0.3); color: var(--text-bright); }

  .msg { display: flex; }
  .msg.user { justify-content: flex-end; }
  .msg.assistant { justify-content: flex-start; }
  .bubble {
    max-width: 88%;
    padding: 8px 11px;
    border-radius: 10px;
    font-size: 12px; line-height: 1.5;
    color: var(--text-1);
    display: flex; flex-direction: column; gap: 6px;
  }
  .msg.user .bubble {
    background: linear-gradient(180deg, #1a7ac9, #0f5994);
    color: white;
    border-bottom-right-radius: 3px;
  }
  .msg.assistant .bubble {
    background: rgba(255,255,255,0.05);
    border: 1px solid var(--border-soft);
    border-bottom-left-radius: 3px;
  }
  .bubble.loading { display: inline-flex; flex-direction: row; align-items: center; gap: 8px; color: var(--text-3); flex-wrap: wrap; }
  .content { white-space: pre-wrap; word-break: break-word; }
  .msg-img {
    max-width: 100%; max-height: 200px;
    border-radius: 6px;
    border: 1px solid rgba(255,255,255,0.08);
    object-fit: cover;
  }
  .sheet-block {
    margin-top: 4px;
    border-top: 1px dashed var(--border-soft);
    padding-top: 6px;
  }
  .sheet-block summary {
    cursor: pointer; font-size: 10.5px; color: var(--text-3); font-weight: 600;
    display: inline-flex; align-items: center; gap: 4px;
    list-style: none;
  }
  .sheet-block summary::-webkit-details-marker { display: none; }
  .sheet-img {
    margin-top: 6px; max-width: 100%;
    border-radius: 6px; border: 1px solid rgba(255,255,255,0.1);
    background: white;
  }
  .stage-thumb-link { display: inline-block; }
  .stage-thumb {
    height: 48px; width: 48px; object-fit: cover;
    border-radius: 4px;
    border: 1px solid rgba(255,255,255,0.15);
    background: white;
  }

  .tool-log {
    margin-top: 2px; padding-top: 6px;
    border-top: 1px dashed var(--border-soft);
    font-size: 10.5px; color: var(--text-muted);
  }
  .tool-log summary {
    cursor: pointer; display: inline-flex; align-items: center; gap: 4px;
    list-style: none; font-weight: 600;
  }
  .tool-log summary::-webkit-details-marker { display: none; }
  .tool-log ul { margin: 4px 0 0; padding-left: 16px; }
  .tool-log code { font-size: 10px; color: var(--text-3); font-family: ui-monospace, monospace; }

  .pending-image {
    display: flex; align-items: center; gap: 8px;
    padding: 8px 10px;
    margin: 0 10px;
    background: rgba(74,184,255,0.08);
    border: 1px solid rgba(74,184,255,0.25);
    border-radius: 8px;
  }
  .pending-image img {
    width: 40px; height: 40px;
    border-radius: 5px; object-fit: cover;
    border: 1px solid rgba(255,255,255,0.1);
  }
  .pending-info { flex: 1; min-width: 0; }
  .pending-name {
    font-size: 11px; font-weight: 600; color: var(--text-bright);
    white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
  }
  .pending-size { font-size: 10px; color: var(--text-3); margin-top: 1px; }

  .composer {
    display: flex; flex-direction: column;
    border-top: 1px solid var(--border-soft);
    background: rgba(255,255,255,0.02);
    position: relative;
  }
  .resize-handle {
    height: 6px; width: 100%;
    cursor: ns-resize;
    background: transparent;
    transition: background 0.12s;
    flex-shrink: 0;
    touch-action: none;
  }
  .resize-handle:hover {
    background: linear-gradient(180deg, transparent, rgba(46,150,255,0.35));
  }
  .resize-handle::before {
    content: '';
    display: block;
    width: 36px; height: 3px;
    margin: 1px auto 0;
    border-radius: 2px;
    background: rgba(255,255,255,0.18);
  }
  .resize-handle:hover::before { background: var(--accent); }
  .composer-row {
    display: flex; gap: 6px; padding: 8px 10px 10px;
    align-items: stretch;
  }
  .composer-row textarea {
    flex: 1; resize: none;
    padding: 8px 10px; font-size: 12px; line-height: 1.45;
    background: var(--bg-3); color: var(--text-bright);
    border: 1px solid var(--border-soft); border-radius: 8px;
    font-family: inherit;
    min-height: 36px;
    max-height: 480px;
    overflow-y: auto;
    box-sizing: border-box;
  }
  .composer-row textarea:focus { outline: none; border-color: var(--accent); }
  .composer-row textarea:disabled { opacity: 0.5; }
  .composer-row .attach {
    width: 36px; align-self: flex-end;
    height: 36px;
    display: inline-flex; align-items: center; justify-content: center;
    background: var(--chip-bg); color: var(--text-2);
    border: 1px solid var(--border-soft); border-radius: 8px;
    cursor: pointer;
    transition: background 0.12s, color 0.12s;
  }
  .composer-row .attach:hover:not(:disabled) { background: var(--chip-bg-hover); color: var(--text-bright); }
  .composer-row .attach:disabled { opacity: 0.35; cursor: not-allowed; }
  .send {
    min-width: 50px;
    height: 36px;
    padding: 0 8px;
    align-self: flex-end;
    display: inline-flex; align-items: center; justify-content: center;
    background: linear-gradient(180deg, #2e96ff, #0f5994);
    color: white; border: 1px solid rgba(255,255,255,0.1); border-radius: 8px;
    cursor: pointer;
    box-shadow: 0 4px 12px -4px rgba(74,184,255,0.5);
    font-size: 11px; font-family: ui-monospace, monospace;
    flex-shrink: 0;
  }
  .send:disabled { opacity: 0.35; cursor: not-allowed; box-shadow: none; }
  .cost-badge {
    font-weight: 700; letter-spacing: 0.02em;
  }
  .cost-badge.warn {
    color: #fff3cd;
    text-shadow: 0 0 6px rgba(255, 193, 7, 0.6);
  }
  .err-bar {
    display: flex; align-items: center; gap: 6px;
    padding: 6px 10px;
    background: rgba(208,74,58,0.15); color: #ffaaa0;
    font-size: 10.5px;
    white-space: pre-wrap;
  }
  .drop-overlay {
    position: absolute; inset: 0;
    display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 8px;
    background: rgba(46,150,255,0.18); color: var(--text-bright);
    font-size: 13px; font-weight: 600;
    backdrop-filter: blur(4px);
    pointer-events: none;
    z-index: 30;
  }
</style>
